/* eslint-disable max-lines */
import {
  DepositLocation,
  GeoCoordinateInput,
  InlineAlertType,
} from '@varo/graphql-schema-types';
import { getCurrentPositionAsync, LocationAccuracy } from 'expo-location';
import {
  NativeStackHeaderProps,
  Stack,
  useFocusEffect,
  useLocalSearchParams,
  useRouter,
} from 'expo-router';
import { useBarcodeExpiration } from 'features/moveMoney/hooks/useBarcodeExpiration';
import { useBarcodeVisual } from 'features/moveMoney/hooks/useBarcodeVisual';
import { useDepositCashFlowFromRoute } from 'features/moveMoney/hooks/useDepositCashFlowFromRoute';
import { useFeeDisplay } from 'features/moveMoney/hooks/useFeeDisplay';
import { useGenerateDepositBarcode } from 'features/moveMoney/hooks/useGenerateDepositBarcode';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AppState, ScrollView, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Rect, Svg } from 'react-native-svg';
import Button from 'shared/components/vxl/Button/Button';
import HairLineDivider from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import SectionDivider from 'shared/components/vxl/divider/SectionDivider/SectionDivider';
import InlineAlert from 'shared/components/vxl/InlineAlert/InlineAlert';
import ListItem from 'shared/components/vxl/ListItem/ListItem';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import Snackbar, {
  SNACKBAR_TIMEOUT,
} from 'shared/components/vxl/Snackbar/Snackbar';
import BodySmText from 'shared/components/vxl/typography/primary/body/BodySmText';
import BodyXSText from 'shared/components/vxl/typography/primary/body/BodyXSText';
import TitleLgText from 'shared/components/vxl/typography/primary/title/TitleLgText';
import { isWeb } from 'shared/constants/platform';
import { useAnalyticsConfiguration } from 'shared/hooks/analytics/useAnalyticsConfiguration';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import {
  type LocationAccess,
  useLocationServices,
} from 'shared/hooks/maps/useLocationServices';
import { useAutoHideSnackbar } from 'shared/hooks/snackBar/useAutoHideSnackbar';
import useIsFeatureEnabled from 'shared/hooks/statsig/useIsFeatureEnabled/useIsFeatureEnabled';
import { FeatureFlagKey } from 'shared/providers/statsig/StatsigProvider.d';
import { VXLComponentId } from 'shared/renderer/types';
import theme from 'shared/styles/theme';
import openMarkerInMaps from 'shared/utils/maps/openMarkerInMaps';
import { logErrorWithContext } from 'shared/utils/sentry/sentry';
import {
  ErrorSeverity,
  FeatureArea,
  ResponsibleTeam,
} from 'shared/utils/sentry/sentry.constants';

import BarcodeErrorScreen from './errorScreens/BarcodeErrorScreen';
import DownloadAppErrorScreen from './errorScreens/DownloadAppErrorScreen';
import LocationServicesErrorScreen from './errorScreens/LocationServicesErrorScreen';

type BarcodeScreenParams = {
  id: string;
  retailerKey: string;
  name: string;
  type: string;
  phoneNumber: string;
  distance: string;
  latitude: string;
  longitude: string;
  customerFee: string;
  isFreeCashDepositLocation: string;
  isBarcodeFreeDepositLocation: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  state: string;
  zip: string;
  flowSource?: string | string[];
};

export default function BarcodeScreen() {
  const router = useRouter();
  const { bottom } = useSafeAreaInsets();
  const params = useLocalSearchParams<BarcodeScreenParams>();
  const { t: translatedText } = useTranslation('moveMoney', {
    keyPrefix: 'depositCash.barcode',
  });
  const { t: translatedTextDepositCash } = useTranslation('moveMoney', {
    keyPrefix: 'depositCash',
  });

  const { flowName } = useDepositCashFlowFromRoute();
  const normalizedFlowName = flowName ?? 'move_money';

  const location: DepositLocation = {
    id: params.id,
    retailerKey: params.retailerKey,
    name: params.name,
    type: params.type as DepositLocation['type'],
    phoneNumber: params.phoneNumber,
    distance: params.distance,
    customerFee: parseFloat(params.customerFee) || 0,
    isFreeCashDepositLocation: params.isFreeCashDepositLocation === 'true',
    isBarcodeFreeDepositLocation:
      params.isBarcodeFreeDepositLocation === 'true',
    coordinate: {
      latitude: parseFloat(params.latitude) || 0,
      longitude: parseFloat(params.longitude) || 0,
    },
    address: {
      addressLine1: params.addressLine1,
      addressLine2: params.addressLine2,
      city: params.city,
      state: params.state,
      zip: params.zip,
    },
  };

  const isKrogerBarcodeEnabled = useIsFeatureEnabled(
    FeatureFlagKey.ENABLE_GD_BARCODE_KROGER,
  );
  const feeDisplay = useFeeDisplay(location, translatedTextDepositCash);

  const {
    isGenerating,
    barcodeError,
    barcodeData,
    errorScreen,
    refreshState,
    generateBarcode,
    resetError,
    resetRefreshError,
    abortGenerateBarcode,
  } = useGenerateDepositBarcode();

  const { access, checkLocationServices, requestPermission, hasPermission } =
    useLocationServices();

  const showRefreshError =
    refreshState.hasRefreshError && refreshState.attempts > 0;

  const { isBarcodeExpired } = useBarcodeExpiration(barcodeData);
  const { barcodeBars, barcodeViewBox } = useBarcodeVisual(barcodeData);

  const { segmentClient } = useAnalyticsConfiguration();
  const hasLoggedRef = useRef(false);
  const hasLoggedBackRef = useRef(false);

  useFocusEffect(
    useCallback(() => {
      return () => {
        hasLoggedRef.current = false;
        hasLoggedBackRef.current = false;
      };
    }, []),
  );

  useEffect(() => {
    const canLogBarcodeScreen =
      !hasLoggedRef.current &&
      !!barcodeData?.barcodeId &&
      !barcodeError &&
      !errorScreen;
    if (canLogBarcodeScreen) {
      void segmentClient.logScreenEvent('Cash Deposit Barcode Display', {
        name: 'Cash Deposit Barcode Display',
        category: 'deposit_cash',
        subcategory: 'barcode',
        flow_name: normalizedFlowName,
      });
      hasLoggedRef.current = true;
    }
  }, [
    barcodeData?.barcodeId,
    barcodeError,
    errorScreen,
    normalizedFlowName,
    segmentClient,
  ]);

  const hasLoggedExpiredRef = useRef(false);
  const previousBarcodeIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (isBarcodeExpired && barcodeData && !hasLoggedExpiredRef.current) {
      void segmentClient.logScreenEvent('Cash Deposit Barcode Expired', {
        name: 'Cash Deposit Barcode Expired',
        category: 'deposit_cash',
        subcategory: 'cash_deposit_barcode',
        barcode_status: 'Expired',
        flow_name: normalizedFlowName,
      });
      hasLoggedExpiredRef.current = true;
    }
  }, [isBarcodeExpired, barcodeData, segmentClient, normalizedFlowName]);

  useEffect(() => {
    if (
      barcodeData?.barcodeId &&
      previousBarcodeIdRef.current &&
      barcodeData.barcodeId !== previousBarcodeIdRef.current
    ) {
      void segmentClient.logScreenEvent(
        'Cash Deposit Barcode Refreshed Banner',
        {
          name: 'Cash Deposit Barcode Refreshed Banner',
          category: 'deposit_cash',
          subcategory: 'barcode',
          flow_name: normalizedFlowName,
        },
      );
      hasLoggedExpiredRef.current = false;
    }
    if (barcodeData?.barcodeId) {
      previousBarcodeIdRef.current = barcodeData.barcodeId;
    }
  }, [barcodeData, segmentClient, normalizedFlowName]);

  const hasLoggedUnableToRefreshRef = useRef(false);

  useEffect(() => {
    if (
      showRefreshError &&
      !isGenerating &&
      !hasLoggedUnableToRefreshRef.current
    ) {
      void segmentClient.logScreenEvent('Barcode Unable To Refresh', {
        name: 'Barcode Unable To Refresh',
        category: 'deposit_cash',
        subcategory: 'cash_deposit_barcode',
        flow_name: normalizedFlowName,
      });
      hasLoggedUnableToRefreshRef.current = true;
    }
    if (!showRefreshError && hasLoggedUnableToRefreshRef.current) {
      hasLoggedUnableToRefreshRef.current = false;
    }
  }, [showRefreshError, isGenerating, segmentClient, normalizedFlowName]);

  const [snackbarKey, setSnackbarKey] = useState(0);
  const [isUserRefreshing, setIsUserRefreshing] = useState(false);
  const [isLocating, setIsLocating] = useState<boolean>(false);
  const isLoadingBarcode = isLocating || isGenerating;

  const shouldShowKrogerFeeNote =
    isKrogerBarcodeEnabled &&
    location.isBarcodeFreeDepositLocation &&
    !isLoadingBarcode;

  const previousAccessRef = useRef<LocationAccess>(access);
  const inFlightRef = useRef<string | null>(null);
  const locationAbortRef = useRef<AbortController | null>(null);

  useFocusEffect(() => {
    void checkLocationServices();
  });

  useEffect(() => {
    let intervalId: ReturnType<typeof setInterval> | undefined;

    const subscription = AppState.addEventListener(
      'change',
      async (nextAppState) => {
        if (nextAppState === 'active') {
          await checkLocationServices();
        }
      },
    );

    if (hasPermission && access === 'servicesOff') {
      void checkLocationServices();

      intervalId = setInterval(() => {
        void checkLocationServices();
      }, 2000);
    }

    previousAccessRef.current = access;

    return () => {
      subscription.remove();
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [access, hasPermission, checkLocationServices, requestPermission]);

  const generateBarcodeWithLocation = useCallback(
    async (isRefresh: boolean = false) => {
      if (!location?.id || !location?.retailerKey) {
        return;
      }

      const token = `${Date.now()}`;
      inFlightRef.current = token;

      locationAbortRef.current?.abort();
      locationAbortRef.current = new AbortController();

      let coordinate: GeoCoordinateInput | undefined;
      let gpsError: Error | null = null;

      try {
        setIsLocating(true);
        const position = await getCurrentPositionAsync({
          accuracy: LocationAccuracy.Balanced,
        });
        if (
          token !== inFlightRef.current ||
          locationAbortRef.current?.signal.aborted
        ) {
          return;
        }
        const { latitude, longitude } = position.coords;
        if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
          gpsError = new Error('Invalid GPS coordinates received');
        } else {
          coordinate = { latitude, longitude };
        }
      } catch (error) {
        gpsError =
          error instanceof Error ? error : new Error('Unknown GPS error');
      } finally {
        if (
          token === inFlightRef.current &&
          !locationAbortRef.current?.signal.aborted
        ) {
          setIsLocating(false);
        }
      }

      if (locationAbortRef.current?.signal.aborted) {
        abortGenerateBarcode();
        return;
      }

      if (gpsError) {
        logErrorWithContext(gpsError, {
          featureArea: FeatureArea.PAYMENTS,
          responsibleTeam: ResponsibleTeam.PAYMENTS_TRANSFER,
          severity: ErrorSeverity.MEDIUM,
          additionalContext: {
            reason: 'GPS_FAILED_BARCODE_GENERATION',
            locationId: location.id,
            retailerKey: location.retailerKey,
            isRefresh,
            accessState: access,
          },
        });
      }

      await generateBarcode(
        {
          retailerKey: location.retailerKey,
          storeId: location.id,
          ...(coordinate && { coordinate }),
        },
        isRefresh,
      );
    },
    [
      location?.id,
      location?.retailerKey,
      generateBarcode,
      access,
      abortGenerateBarcode,
    ],
  );

  useEffect(() => {
    if (
      access === 'granted' &&
      location?.id &&
      location?.retailerKey &&
      !barcodeData &&
      !isGenerating &&
      !barcodeError &&
      !errorScreen
    ) {
      void generateBarcodeWithLocation();
    }
  }, [
    access,
    location?.id,
    location?.retailerKey,
    barcodeData,
    isGenerating,
    barcodeError,
    errorScreen,
    generateBarcodeWithLocation,
  ]);

  useEffect(() => {
    if (!isUserRefreshing || isGenerating) {
      return;
    }

    setIsUserRefreshing(false);

    if (!showRefreshError && !barcodeError && !errorScreen && barcodeData) {
      setSnackbarKey((prev) => prev + 1);
    }
  }, [
    isUserRefreshing,
    isGenerating,
    showRefreshError,
    barcodeError,
    errorScreen,
    barcodeData,
  ]);

  const hideSnackbar = useCallback(() => {
    setSnackbarKey(0);
  }, []);

  useAutoHideSnackbar(snackbarKey > 0, hideSnackbar, SNACKBAR_TIMEOUT);

  const retryBarcodeFromError = () => {
    resetError();
    void generateBarcodeWithLocation();
  };

  const handleRefreshExpiredAnalytics = usePressableAnalytics({
    analyticsId: 'refreshExpired',
    customAttributes: {
      source_name: 'cash_deposit_location_map',
      category: 'deposit_cash',
      subcategory: 'cash_deposit_barcode',
      barcode_status: 'Expired',
      flow_name: normalizedFlowName,
    },
  });

  const refreshExpiredBarcode = () => {
    handleRefreshExpiredAnalytics();
    setIsUserRefreshing(true);
    void generateBarcodeWithLocation(true);
  };

  const formattedAddress = useMemo(() => {
    if (!location?.address) {
      return translatedText('defaultRetailerAddress');
    }

    const { addressLine1, addressLine2, city, state, zip } = location.address;
    const parts = [
      addressLine1,
      addressLine2,
      city,
      state && zip ? `${state} ${zip}` : state || zip,
    ].filter(Boolean);

    if (parts.length === 0) {
      return translatedText('defaultRetailerAddress');
    }

    return parts.join(', ');
  }, [location?.address, translatedText]);

  const distanceDisplay =
    location?.distance || translatedText('defaultDistance');

  const locationTitle = location?.name || translatedText('defaultRetailerName');

  const locationForMaps = location;

  const handleOpenInMapsClickedAnalytics = usePressableAnalytics({
    analyticsId: 'openInMapsClicked',
    customAttributes: {
      source_name: 'deposit_cash_barcode',
      category: 'deposit_cash',
      subcategory: 'barcode',
      flow_name: normalizedFlowName,
    },
  });

  const handleOpenInMaps = () => {
    if (locationForMaps) {
      openMarkerInMaps(locationForMaps);
      handleOpenInMapsClickedAnalytics();
    }
  };

  const handleCloseRefreshErrorAnalytics = usePressableAnalytics({
    analyticsId: 'close',
    customAttributes: {
      category: 'deposit_cash',
      source_name: 'cash_deposit_location_map',
      subcategory: 'cash_deposit_barcode',
      flow_name: normalizedFlowName,
    },
  });

  const handleReturnToMapAnalytics = usePressableAnalytics({
    analyticsId: 'returnToMap',
    customAttributes: {
      source_name: 'deposit_cash_barcode',
      category: 'deposit_cash',
      subcategory: 'barcode',
      flow_name: normalizedFlowName,
    },
  });

  const dismissRefreshError = () => {
    if (!isBarcodeExpired && showRefreshError) {
      handleCloseRefreshErrorAnalytics();
      resetRefreshError();
    }
  };

  useEffect(() => {
    return () => {
      locationAbortRef.current?.abort();
      abortGenerateBarcode();
      inFlightRef.current = null;
    };
  }, [abortGenerateBarcode]);

  if (isWeb) {
    return <DownloadAppErrorScreen />;
  }

  if (access === 'servicesOff' || access === 'permissionDenied') {
    return (
      <LocationServicesErrorScreen
        access={access}
        hasPermission={hasPermission}
        checkLocationServices={checkLocationServices}
        requestPermission={requestPermission}
      />
    );
  }

  if (access === 'checking') {
    return (
      <>
        <Stack.Screen
          options={{
            title: '',
            headerShown: true,
            headerBackVisible: true,
            header: (props: NativeStackHeaderProps) => (
              <ExpoNavigationBar
                {...props}
                primaryOnPress={() => {
                  if (!hasLoggedBackRef.current) {
                    handleReturnToMapAnalytics();
                    hasLoggedBackRef.current = true;
                  }

                  if (router.canGoBack()) {
                    router.back();
                  } else {
                    router.replace('/(authenticated)/(tabs)');
                  }
                }}
              />
            ),
          }}
        />
        <View style={styles.webErrorContainer}>
          <LoadingSpinner
            fill={theme.color.text.secondary}
            accessibilityLabel="loading location"
          />
        </View>
      </>
    );
  }

  if (!location) {
    return <BarcodeErrorScreen onRetry={retryBarcodeFromError} />;
  }

  if (errorScreen || barcodeError) {
    return (
      <BarcodeErrorScreen
        onRetry={retryBarcodeFromError}
        messageHeadline={errorScreen?.messageHeadline}
        messageBody={errorScreen?.messageBody}
        hideRetryButton={errorScreen?.hideRetryButton}
      />
    );
  }

  return (
    <>
      <Stack.Screen
        options={{
          title: '',
          headerShown: true,
          headerBackVisible: true,
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar
              {...props}
              primaryOnPress={() => {
                if (!hasLoggedBackRef.current) {
                  handleReturnToMapAnalytics();
                  hasLoggedBackRef.current = true;
                }

                if (router.canGoBack()) {
                  router.back();
                } else {
                  router.replace('/(authenticated)/(tabs)');
                }
              }}
            />
          ),
        }}
      />
      {snackbarKey > 0 ? (
        <Snackbar
          text={translatedText('newBarcodeCreated')}
          duration={SNACKBAR_TIMEOUT}
        />
      ) : null}
      <View style={styles.container}>
        <ScrollView
          style={styles.scrollView}
          contentContainerStyle={styles.scrollViewContent}
          showsVerticalScrollIndicator={true}
        >
          {showRefreshError ? (
            <View style={styles.refreshErrorContainer}>
              <InlineAlert
                alertType={InlineAlertType.Error}
                bodyText={translatedText('refreshFailedError')}
                onDismiss={dismissRefreshError}
                accessibilityLabel="Barcode refresh failed error"
              />
            </View>
          ) : null}
          <View
            style={[styles.content, bottom ? { paddingBottom: bottom } : null]}
          >
            <View style={styles.retailerSection}>
              <TitleLgText numberOfLines={1} style={styles.retailerName}>
                {locationTitle}
              </TitleLgText>
              <ListItem
                primaryCopy={formattedAddress}
                secondaryCopy={distanceDisplay}
                primaryNumberOfLines={2}
                showsChevron={false}
                trailingAccessory={
                  feeDisplay
                    ? {
                        id: 'fee-tag',
                        name: VXLComponentId.TAG,
                        props: {
                          text: feeDisplay.text,
                          type: feeDisplay.type,
                        },
                      }
                    : null
                }
              />

              {shouldShowKrogerFeeNote ? (
                <BodyXSText style={styles.krogerFeeNote}>
                  {translatedText('krogerFeeNote')}
                </BodyXSText>
              ) : null}
              <View style={styles.buttonWrapper}>
                <Button
                  label={translatedText('openInMaps')}
                  type="SECONDARY"
                  onPress={handleOpenInMaps}
                />
              </View>
            </View>

            <View style={styles.sectionSpacer} />
            <SectionDivider labelText={translatedText('instructionTitle')} />
            <View style={styles.barcodeSection}>
              <View style={styles.barcodeContainer}>
                {isLoadingBarcode ? (
                  <View style={styles.loadingSpinnerContainer}>
                    <LoadingSpinner
                      fill={theme.color.text.secondary}
                      accessibilityLabel="loading barcode"
                    />
                  </View>
                ) : null}
                {!isLoadingBarcode && barcodeBars.length > 0 ? (
                  <>
                    <View style={styles.barcodeWrapper}>
                      <Svg
                        width="100%"
                        height={barcodeViewBox.height}
                        viewBox={`${barcodeViewBox.minX} 0 ${barcodeViewBox.width} ${barcodeViewBox.height}`}
                        preserveAspectRatio="xMidYMid meet"
                      >
                        {barcodeBars.map((bar) => (
                          <Rect
                            key={`bar-${bar.x}-${bar.width}`}
                            x={bar.x}
                            y={0}
                            width={bar.width}
                            height={barcodeViewBox.height}
                            fill={theme.color.interactive.black}
                          />
                        ))}
                      </Svg>
                    </View>
                    <BodyXSText style={styles.barcodeId}>
                      {barcodeData?.barcodeId}
                    </BodyXSText>
                  </>
                ) : null}

                {(isBarcodeExpired || showRefreshError) && !isLoadingBarcode ? (
                  <BodySmText
                    numberOfLines={1}
                    style={styles.barcodeExpiredText}
                  >
                    {translatedText('barcodeExpired')}
                  </BodySmText>
                ) : null}
                {(isBarcodeExpired || showRefreshError) && !isLoadingBarcode ? (
                  <View style={styles.refreshBarcodeContainer}>
                    <Button
                      label={translatedText('refreshBarcode')}
                      type="TEXT"
                      size="LARGE"
                      width="FULL"
                      onPress={refreshExpiredBarcode}
                    />
                  </View>
                ) : null}
              </View>
              <View style={styles.dividerContainer}>
                <HairLineDivider />
              </View>
              <View style={styles.textContainer}>
                <BodyXSText numberOfLines={0} style={styles.limitText}>
                  {translatedText('limitText')}
                </BodyXSText>

                <BodyXSText numberOfLines={0} style={styles.disclaimerText}>
                  {translatedText('disclaimerText')}
                </BodyXSText>
              </View>
            </View>
          </View>
        </ScrollView>
      </View>
    </>
  );
}

const styles = StyleSheet.create({
  barcodeContainer: {
    alignItems: 'center',
    alignSelf: 'stretch',
    flexDirection: 'column',
    justifyContent: 'center',
    marginVertical: theme.spacing.s,
  },
  barcodeExpiredText: {
    color: theme.color.text.secondary,
    textAlign: 'center',
  },
  refreshBarcodeContainer: {
    width: '100%',
    paddingHorizontal: theme.spacing.m,
    paddingTop: theme.spacing.m,
  },
  refreshErrorContainer: {
    width: '100%',
    paddingHorizontal: theme.spacing.m,
    backgroundColor: theme.color.background.primary,
  },
  barcodeSection: {
    alignItems: 'stretch',
    backgroundColor: theme.color.background.primary,
    width: '100%',
  },
  barcodeId: {
    color: theme.color.text.primary,
    marginBottom: theme.spacing.s,
    textAlign: 'center',
  },
  barcodeWrapper: {
    alignItems: 'center',
    alignSelf: 'stretch',
    aspectRatio: 343.0 / 94.37,
    justifyContent: 'center',
    marginHorizontal: theme.spacing.xs,
  },
  buttonWrapper: {
    paddingHorizontal: theme.spacing.m,
    paddingTop: theme.spacing.m,
  },
  krogerFeeNote: {
    color: theme.color.text.secondary,
    paddingHorizontal: theme.spacing.m,
    textAlign: 'left',
  },
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  content: {
    flexGrow: 1,
  },
  disclaimerText: {
    color: theme.color.text.secondary,
    flexShrink: 1,
  },
  sectionSpacer: {
    backgroundColor: theme.color.background.surface,
    height: theme.spacing.s,
  },
  limitText: {
    color: theme.color.text.secondary,
    flexShrink: 1,
  },
  loadingSpinnerContainer: {
    alignItems: 'center',
    alignSelf: 'stretch',
    aspectRatio: 343.0 / 94.37,
    justifyContent: 'center',
  },
  retailerName: {
    marginHorizontal: theme.spacing.m,
  },
  retailerSection: {
    paddingVertical: theme.spacing.m,
  },
  dividerContainer: {
    marginHorizontal: theme.spacing.m,
  },
  textContainer: {
    flexDirection: 'column',
    gap: theme.spacing.m,
    margin: theme.spacing.m,
  },
  scrollView: {
    flex: 1,
  },
  scrollViewContent: {
    flexGrow: 1,
  },
  webErrorContainer: {
    alignItems: 'center',
    backgroundColor: theme.color.background.primary,
    flex: 1,
    justifyContent: 'space-between',
    padding: theme.spacing.m,
  },
});
